import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type ColourTheme =
  | "SYSTEM"
  | "LIGHT"
  | "DARK";

export type InterfaceStyle =
  | "LIQUID_GLASS"
  | "SPATIAL"
  | "MINIMAL";

type ThemeContextValue = {
  colourTheme: ColourTheme;
  interfaceStyle: InterfaceStyle;
  setColourTheme: (value: ColourTheme) => void;
  setInterfaceStyle: (value: InterfaceStyle) => void;
};

const ThemeContext =
  createContext<ThemeContextValue | null>(null);

const THEME_KEY = "saradium-colour-theme";
const STYLE_KEY = "saradium-interface-style";

function storedTheme(): ColourTheme {
  const value = localStorage.getItem(THEME_KEY);

  return value === "LIGHT" || value === "DARK"
    ? value
    : "SYSTEM";
}

function storedStyle(): InterfaceStyle {
  const value = localStorage.getItem(STYLE_KEY);

  return value === "SPATIAL" || value === "MINIMAL"
    ? value
    : "LIQUID_GLASS";
}

export function ThemeProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [colourTheme, setColourTheme] =
    useState<ColourTheme>(storedTheme);

  const [interfaceStyle, setInterfaceStyle] =
    useState<InterfaceStyle>(storedStyle);

  useEffect(() => {
    const root = document.documentElement;

    function applyTheme() {
      const dark =
        colourTheme === "DARK" ||
        (
          colourTheme === "SYSTEM" &&
          window.matchMedia(
            "(prefers-color-scheme: dark)",
          ).matches
        );

      root.dataset.theme =
        dark ? "dark" : "light";

      root.dataset.style =
        interfaceStyle
          .toLowerCase()
          .replace("_", "-");
    }

    applyTheme();

    const media = window.matchMedia(
      "(prefers-color-scheme: dark)",
    );

    media.addEventListener("change", applyTheme);

    return () =>
      media.removeEventListener(
        "change",
        applyTheme,
      );
  }, [colourTheme, interfaceStyle]);

  useEffect(() => {
    localStorage.setItem(
      THEME_KEY,
      colourTheme,
    );
  }, [colourTheme]);

  useEffect(() => {
    localStorage.setItem(
      STYLE_KEY,
      interfaceStyle,
    );
  }, [interfaceStyle]);

  const value = useMemo(
    () => ({
      colourTheme,
      interfaceStyle,
      setColourTheme,
      setInterfaceStyle,
    }),
    [colourTheme, interfaceStyle],
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error(
      "useTheme must be used inside ThemeProvider.",
    );
  }

  return context;
}